import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { getUserProfile } from '@/lib/api/user';
import { useUserStore } from '@/stores/userStore';

interface ProfileGuardProps {
  children: React.ReactNode;
}

/**
 * ProfileGuard component checks if user has completed their profile.
 * If profile is incomplete (temporary gamertag), redirects to signup page.
 * Also populates the user store with the fetched profile data.
 */
export function ProfileGuard({ children }: ProfileGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken, isLoaded } = useAuth();
  const { setUser } = useUserStore();
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    async function checkProfile() {
      // Prevent multiple simultaneous checks
      if (hasCheckedRef.current) {
        return;
      }

      if (!isLoaded) {
        setIsChecking(false);
        return;
      }

      if (!getToken) {
        setIsChecking(false);
        return;
      }

      // Don't redirect if already on signup page
      if (location.pathname === '/signup') {
        setIsChecking(false);
        return;
      }

      hasCheckedRef.current = true;

      try {
        const token = await getToken();
        if (!token) {
          setIsChecking(false);
          hasCheckedRef.current = false;
          return;
        }

        // Add timeout to prevent hanging
        const timeoutId = setTimeout(() => {
          setError('Request timed out. Please check if the backend server is running on port 8788.');
          setIsChecking(false);
          hasCheckedRef.current = false;
        }, 10000); // 10 second timeout

        const profile = await getUserProfile(getToken);
        clearTimeout(timeoutId);
        
        // Populate user store with profile data
        setUser(profile);
        
        // Check if profile is incomplete (temporary gamertag starts with "user_")
        if (profile.gamertag.startsWith('user_')) {
          navigate('/signup', { replace: true });
          hasCheckedRef.current = false;
          return;
        }

        setIsChecking(false);
        setError(null);
      } catch (error) {
        // If profile fetch fails, show error but allow through
        const errorMessage = error instanceof Error 
          ? error.message 
          : 'Failed to load user profile. Please ensure the backend server is running on port 8788.';
        
        console.error('Failed to check profile:', error);
        setError(errorMessage);
        setIsChecking(false);
        hasCheckedRef.current = false;
      }
    }

    checkProfile();
  }, [navigate, getToken, isLoaded, location.pathname]); // Removed setUser from deps

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading user data...</p>
          {error && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400 max-w-md">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-4">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Please ensure the backend server is running:
          </p>
          <code className="block bg-gray-100 dark:bg-gray-800 p-2 rounded text-sm mb-4">
            npm run dev:worker
          </code>
          <button
            onClick={() => {
              hasCheckedRef.current = false;
              setIsChecking(true);
              setError(null);
            }}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

